import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button/Button";
import { Dialog } from "components/ui/Dialog/Dialog";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./PcrsCredentialsDialog.module.css";

const credentialsSchema = z.object({
  username: z.string().trim().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export const PcrsCredentialsDialog = ({
  open,
  onClose,
  onSave,
  defaultUsername = "",
  errorMessage,
  zIndex = 1400,
}) => {
  const methods = useForm({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { username: defaultUsername, password: "" },
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleSave = async (values) => {
    await onSave(values);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="PCRS credentials"
      width={440}
      zIndex={zIndex}
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(handleSave, onInvalidSubmit("PcrsCredentialsDialog"))}>
          {!!errorMessage && <p className={styles.error}>{errorMessage}</p>}
          <p className={styles.description}>
            Enter your PCRS username and password. They are encrypted and stored
            in this browser and used to connect to PCRS for CDM.
          </p>

          <FormField name="username" label="Username">
            <CustomInput
              type="text"
              autoComplete="username"
              {...register("username")}
            />
          </FormField>

          <FormField name="password" label="Password">
            <CustomInput
              type="password"
              autoComplete="current-password"
              {...register("password")}
            />
          </FormField>

          <div className={styles.actions}>
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};